"use client";

import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { parseISO } from "date-fns";
import { useCallback } from "react";
import {
  type AddonQuantityEntry,
  buildAddonGtmItem,
  findAddonCategoryName,
  findAddonInCategories,
  resolveAddonUnitPrice,
} from "@/features/addons/lib/addon-gtm-item";
import type { ConfigurationItemAddon } from "@/features/addons/types/configuration-item-addon.type";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { sendEvent } from "../gtm/event";

type AddonSnapshot = {
  quantity: number;
  dates: Date[];
  entries: AddonQuantityEntry[];
};

const groupByAddonId = (
  entries: ConfigurationItemAddon[],
): Map<string, AddonSnapshot> => {
  const grouped = new Map<string, AddonSnapshot>();
  for (const entry of entries) {
    const current = grouped.get(entry.addonId) ?? {
      quantity: 0,
      dates: [],
      entries: [],
    };
    current.quantity += entry.quantity;
    current.dates.push(parseISO(entry.date));
    current.entries.push({ date: entry.date, quantity: entry.quantity });
    grouped.set(entry.addonId, current);
  }
  return grouped;
};

type ReportAddonCartChangesParams = {
  previous: ConfigurationItemAddon[];
  next: ConfigurationItemAddon[];
  categories: DietAddonGroupDietAddonsRead[];
  cateringName?: string;
  // Addon quote of the position from before the change. It prices what is being
  // removed — the entries are still in it, at the amounts the summary showed.
  // Additions are deliberately not priced from a quote: the addon dialog offers
  // them at the catalog price, and an add_to_cart has to report the price the
  // user saw, the same one the configuration view reports (MPL-3873).
  pricing?: SubItemsPricing | null;
};

export const useAddonCartChangeEvents = () =>
  useCallback(
    ({
      previous,
      next,
      categories,
      cateringName,
      pricing,
    }: ReportAddonCartChangesParams) => {
      const before = groupByAddonId(previous);
      const after = groupByAddonId(next);

      const report = (
        eventName: "add_to_cart" | "remove_from_cart",
        addonId: string,
        snapshot: AddonSnapshot,
        quote?: SubItemsPricing | null,
      ) => {
        const addon = findAddonInCategories(categories, addonId);
        if (!addon) {
          return;
        }
        const unitPrice = resolveAddonUnitPrice({
          addon,
          entries: snapshot.entries,
          pricing: quote,
        });
        sendEvent({
          event: eventName,
          ecommerce: {
            currency: "PLN",
            value: unitPrice * snapshot.quantity,
            items: [
              buildAddonGtmItem({
                addon,
                cateringName,
                categoryName: findAddonCategoryName(categories, addonId),
                quantity: snapshot.quantity,
                selectedDates: snapshot.dates,
                unitPrice,
              }),
            ],
          },
        });
      };

      for (const [addonId, snapshot] of after) {
        if (!before.has(addonId)) {
          report("add_to_cart", addonId, snapshot);
        }
      }

      for (const [addonId, snapshot] of before) {
        if (!after.has(addonId)) {
          report("remove_from_cart", addonId, snapshot, pricing);
        }
      }
    },
    [],
  );
